import { Check, CircleAlert, Copy, Download, ShieldCheck } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import QRCode from 'react-qr-code';

import { Spinner } from '@/components/auth-frame/Spinner';
import { Alert, AlertDescription } from '@/components/lumen/alert';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { useLanguage } from '@contexts/LanguageContext';
import { cn } from '@/lib/utils';

import { MFACodeInput } from './MFACodeInput';

export interface MFASetupDialogProps {
  opened: boolean;
  qrCodeUrl: string;
  secret: string;
  backupCodes: string[];
  deviceId: number;
  onVerify: (code: string) => Promise<void>;
  onClose: () => void;
  isVerifying?: boolean;
  /** Why the last code was refused, shown under the boxes. */
  error?: string;
}

const STEPS = [
  { label: 'lumen.auth.setup.step1', description: 'lumen.auth.setup.step1Desc' },
  { label: 'lumen.auth.setup.step2', description: 'lumen.auth.setup.step2Desc' },
  { label: 'lumen.auth.setup.step3', description: 'lumen.auth.setup.step3Desc' },
] as const;

/** How long "Tersalin!" stays up (Mantine CopyButton's default). */
const COPIED_MS = 1000;

/**
 * First-time MFA setup (was MFASetupModal): scan the QR or type the secret, download the backup
 * codes, then confirm a code. It is a required step of the first sign-in, so like the old modal it
 * has no close button and ignores Escape and outside clicks; `onClose` is kept for the contract.
 */
export function MFASetupDialog({
  opened,
  qrCodeUrl,
  secret,
  backupCodes,
  deviceId,
  onVerify,
  onClose,
  isVerifying = false,
  error,
}: MFASetupDialogProps) {
  const { t } = useLanguage();
  const [active, setActive] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodesSaved, setBackupCodesSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);
  const errorId = useId();

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const handleDownloadBackupCodes = () => {
    const content = `LUMEN MFA BACKUP CODES
=========================

PENTING: Simpan kode-kode ini di tempat yang aman!
Kode backup ini dapat digunakan untuk login jika Anda kehilangan akses ke perangkat autentikasi.

${backupCodes.map((code, idx) => `${idx + 1}. ${code}`).join('\n')}

Tanggal Download: ${new Date().toLocaleString('id-ID')}
Device ID: ${deviceId}

=========================
JANGAN BAGIKAN KODE INI KEPADA SIAPAPUN
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lumen-backup-codes-${new Date().getTime()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    setBackupCodesSaved(true);
  };

  const handleCopySecret = () => {
    void navigator.clipboard?.writeText(secret).then(() => {
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
    });
  };

  const handleVerify = async () => {
    if (verificationCode.length === 6) {
      await onVerify(verificationCode);
    }
  };

  const nextStep = () => setActive((current) => (current < 2 ? current + 1 : current));
  const prevStep = () => setActive((current) => (current > 0 ? current - 1 : current));

  const [hintBefore, hintAfter] = t('lumen.auth.setup.scanHint').split('{app}');

  return (
    <Dialog
      open={opened}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="gap-6 sm:max-w-xl"
        // Focus the dialog itself, not its first button: that is the copy button, whose tooltip
        // would pop up on open.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (event.target instanceof HTMLElement) event.target.focus();
        }}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="items-center text-center sm:text-center">
          <ShieldCheck className="size-11 text-label" aria-hidden="true" />
          <DialogTitle className="text-xl">{t('lumen.auth.setup.title')}</DialogTitle>
        </DialogHeader>

        {/* Stepper: steps already passed can be revisited, later ones cannot (as before). */}
        <nav aria-label={t('lumen.auth.setup.steps')}>
          <ol className="grid grid-cols-3 gap-2">
            {STEPS.map((step, index) => {
              const done = index < active;
              const current = index === active;
              return (
                <li key={step.label} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => setActive(index)}
                    disabled={!done}
                    aria-current={current ? 'step' : undefined}
                    className="flex w-full min-w-0 items-start gap-2 rounded-control p-1 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-label/40 enabled:hover:bg-tint disabled:cursor-default"
                  >
                    <span
                      className={cn(
                        'flex size-7 shrink-0 items-center justify-center rounded-full border font-semibold text-xs',
                        done && 'border-transparent bg-grad-btn text-white',
                        current && 'border-label text-label',
                        !done && !current && 'border-line-control text-fg-muted'
                      )}
                    >
                      {done ? <Check className="size-3.5" aria-hidden="true" /> : index + 1}
                    </span>
                    <span className="grid min-w-0">
                      <span className={cn('font-semibold text-13 leading-5', current ? 'text-fg' : 'text-fg-2')}>
                        {t(step.label)}
                      </span>
                      <span className="hidden text-fg-muted text-xs sm:block">{t(step.description)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        {active === 0 && (
          <div className="grid gap-4">
            <Alert variant="info">
              <CircleAlert aria-hidden="true" />
              <AlertDescription>
                <p>
                  {hintBefore}
                  <strong className="text-fg">Google Authenticator</strong>
                  {hintAfter}
                </p>
              </AlertDescription>
            </Alert>

            <div className="grid justify-items-center gap-4 rounded-card border border-line bg-surface-2 p-6">
              {/* White behind the code in both modes, or phones can't scan it. */}
              <div className="rounded-card bg-white p-4">
                <QRCode value={qrCodeUrl} size={200} level="H" title={t('lumen.auth.setup.qrLabel')} />
              </div>

              <div className="flex w-full items-center gap-3 text-fg-muted text-xs">
                <span className="h-px flex-1 bg-line" />
                {t('lumen.auth.setup.manualEntry')}
                <span className="h-px flex-1 bg-line" />
              </div>

              <div className="flex max-w-full items-center gap-1">
                <code className="break-all font-mono font-semibold text-fg text-sm">{secret}</code>
                <Tooltip open={copied || undefined}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={handleCopySecret}
                      aria-label={copied ? t('lumen.auth.setup.copied') : t('lumen.auth.setup.copy')}
                      className={copied ? 'text-ok' : 'text-label'}
                    >
                      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{copied ? t('lumen.auth.setup.copied') : t('lumen.auth.setup.copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={nextStep}>{t('lumen.auth.setup.toBackup')}</Button>
            </div>
          </div>
        )}

        {active === 1 && (
          <div className="grid gap-4">
            <Alert variant="warn">
              <CircleAlert aria-hidden="true" />
              <AlertDescription>
                <p>
                  <strong className="text-fg">{t('lumen.auth.setup.important')}</strong>{' '}
                  {t('lumen.auth.setup.backupWarning')}
                </p>
              </AlertDescription>
            </Alert>

            <div className="grid gap-2 rounded-card border border-line bg-surface-2 p-4">
              <p className="font-semibold text-fg text-sm">{t('lumen.auth.setup.yourCodes')}</p>
              <ol className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, idx) => (
                  <li
                    key={code}
                    className="rounded-control border border-line bg-surface px-2 py-1.5 text-center font-mono text-fg text-sm"
                  >
                    {idx + 1}. {code}
                  </li>
                ))}
              </ol>
            </div>

            <Button
              variant="outline"
              onClick={handleDownloadBackupCodes}
              className={cn('w-full', backupCodesSaved && 'text-ok')}
            >
              {backupCodesSaved ? <Check aria-hidden="true" /> : <Download aria-hidden="true" />}
              {backupCodesSaved ? t('lumen.auth.setup.downloaded') : t('lumen.auth.setup.download')}
            </Button>

            <div className="flex justify-between gap-2">
              <Button variant="ghost" onClick={prevStep}>
                {t('lumen.auth.setup.back')}
              </Button>
              <Button onClick={nextStep} disabled={!backupCodesSaved}>
                {t('lumen.auth.setup.toVerify')}
              </Button>
            </div>
          </div>
        )}

        {active === 2 && (
          <div className="grid gap-4">
            <Alert variant="info">
              <CircleAlert aria-hidden="true" />
              <AlertDescription>
                <p>{t('lumen.auth.setup.verifyHint')}</p>
              </AlertDescription>
            </Alert>

            <div className="grid justify-items-center gap-2">
              <MFACodeInput
                autoFocus
                value={verificationCode}
                onChange={setVerificationCode}
                invalid={Boolean(error)}
                describedBy={error ? errorId : undefined}
              />
              {error && !isVerifying && (
                <p id={errorId} className="text-center font-medium text-err text-sm" role="alert">
                  {error}
                </p>
              )}
            </div>

            <div className="flex justify-between gap-2">
              <Button variant="ghost" onClick={prevStep}>
                {t('lumen.auth.setup.back')}
              </Button>
              <Button
                onClick={handleVerify}
                disabled={verificationCode.length !== 6 || isVerifying}
                aria-busy={isVerifying || undefined}
              >
                {isVerifying && <Spinner />}
                {t('lumen.auth.setup.verifySubmit')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MFASetupDialog;
